import useQueryDevelopments from '@hooks/useQueryDevelopments'
import { RootState } from '@redux/store'
import {
  setConstructionStatusFilter,
  setLocationFilter,
  setTypeFilter,
} from '@slices/developmentsSlice'
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { RealEstateDataPageComponentData, TypeTokko } from 'types'

const GridDevelopments7 = (props: RealEstateDataPageComponentData) => {
  const { global_data } = props
  const { data: allDevelopmentsData, isLoading } = useQueryDevelopments(
    global_data.pageConfig.keys.tokko,
  )
  const dispatch = useDispatch()

  const constructionStatusFilter = useSelector(
    (state: RootState) => state.developments.constructionStatus,
  )

  const developmentFilters = useSelector((state: RootState) => state.developments)

  const locationFilter = useSelector((state: RootState) => state.developments.location)

  const typeFilter = useSelector((state: RootState) => state.developments.type)

  const [isMounted, setIsMounted] = useState(false)

  const devCard = global_data.content?.cardsDevelopments?.type_id ?? '1'
  const Card = require(`@components/cardsDevelopments/${devCard}`)?.default

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const getColSize = (index: number): number => {
    if (index < 2) {
      return 6
    }
    const positionInCycle = (index - 2) % 8
    if (positionInCycle === 0 || positionInCycle === 5) {
      return 12
    }
    return 6
  }

  const convertStatusIdToString = (statusId: number | string): string => {
    const statusMap: { [key: string]: string } = {
      '1': 'Desconocido',
      '2': 'Reuniendo inversores',
      '3': 'En pozo',
      '4': 'En construcción',
      '5': 'Detenido',
      '6': 'Terminado',
    }
    return statusMap[statusId.toString()] || statusId.toString().toUpperCase()
  }

  const constructionStatusOptions = useMemo(() => {
    const statusSet = new Set(['Ver todos'])
    return [
      { name: 'Ver todos', value: 0 },
      ...(allDevelopmentsData ?? []).reduce(
        (buttons, dev) => {
          const status = convertStatusIdToString(dev.construction_status)
          if (!statusSet.has(status)) {
            statusSet.add(status)
            buttons.push({ name: status, value: buttons.length + 1 })
          }
          return buttons
        },
        [] as { name: string; value: number }[],
      ),
    ]
  }, [allDevelopmentsData])

  const filteredDevelopments = React.useMemo(() => {
    if (!allDevelopmentsData) return []

    return allDevelopmentsData.filter((development) => {
      const statusMatch =
        developmentFilters.constructionStatus === 'Ver todos' ||
        !developmentFilters.constructionStatus ||
        convertStatusIdToString(development.construction_status).toLowerCase() ===
          developmentFilters.constructionStatus.toLowerCase()

      const locationMatch = !developmentFilters.location
        ? true
        : development?.location?.name
            ?.toLowerCase()
            .includes(developmentFilters.location.toLowerCase())

      return statusMatch && locationMatch
    })
  }, [allDevelopmentsData, developmentFilters])

  const uniqueLocations = useMemo(() => {
    return [
      ...new Set(
        filteredDevelopments
          .filter(
            (dev) =>
              constructionStatusFilter === 'Ver todos' ||
              constructionStatusFilter === '' ||
              convertStatusIdToString(dev.construction_status) === constructionStatusFilter,
          )
          .map((dev) => dev.location.name),
      ),
    ]
  }, [filteredDevelopments, constructionStatusFilter])

  const uniqueTypes = useMemo(() => {
    return [
      ...new Set(
        filteredDevelopments
          .filter(
            (dev) =>
              constructionStatusFilter === 'Ver todos' ||
              constructionStatusFilter === '' ||
              convertStatusIdToString(dev.construction_status) === constructionStatusFilter,
          )
          .map((dev) => (dev.type as TypeTokko).name),
      ),
    ]
  }, [filteredDevelopments, constructionStatusFilter])

  const handleStatusChange = (status: string) => {
    dispatch(setConstructionStatusFilter(status))
    dispatch(setLocationFilter(''))
    dispatch(setTypeFilter(''))
  }

  const handleLocationChange = (location: string) => {
    dispatch(setLocationFilter(location))
    dispatch(setTypeFilter(''))
  }

  const handleTypeChange = (type: string) => {
    dispatch(setTypeFilter(type))
  }

  return (
    isMounted && (
      <section
        id="grid-development"
        className="p-y-config gridDevelopments7"
      >
        <Container fluid>
          <Row className="position-relative">
            <Col
              xs={12}
              lg={6}
              style={{ position: 'absolute', top: '0', left: '0' }}
            >
              <div className="d-lg-flex d-block align-items-center gap-5">
                <p className="text-bold mb-0 d-none d-lg-block">Filtrar por:</p>
                <div className="content-select">
                  <p className="selected-value">{constructionStatusFilter || 'Estado actual'}</p>
                  <select
                    onChange={(e) => {
                      handleStatusChange(e.target.value)
                    }}
                    value={constructionStatusFilter}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Estado actual
                    </option>
                    {constructionStatusOptions.map((option, index) => (
                      <option
                        key={index}
                        value={option.name}
                      >
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <i />
                </div>
                <div className="content-select">
                  <p className="selected-value">{locationFilter || 'Ciudad'}</p>
                  <select
                    onChange={(e) => {
                      handleLocationChange(e.target.value)
                    }}
                    value={locationFilter}
                  >
                    <option
                      value=""
                      disabled
                    >
                      {locationFilter.length > 0 ? locationFilter : 'Ciudad'}
                    </option>
                    {uniqueLocations.map((option, index) => (
                      <option
                        key={index}
                        value={option}
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                  <i />
                </div>
                <div className="content-select">
                  <p className="selected-value">{typeFilter || 'Tipo'}</p>
                  <select
                    onChange={(e) => {
                      handleTypeChange(e.target.value)
                    }}
                    value={typeFilter}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Tipo
                    </option>
                    {uniqueTypes.map((option, index) => (
                      <option
                        key={index}
                        value={option}
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                  <i />
                </div>
              </div>
            </Col>
            {isLoading
              ? [1, 2, 3].map((index) => (
                  <Col
                    xs={12}
                    lg={4}
                    key={index}
                  >
                    <Card
                      skeleton={true}
                      global_data={global_data}
                    />
                  </Col>
                ))
              : filteredDevelopments?.map((development, index) => (
                  <Col
                    xs={12}
                    lg={getColSize(index)}
                    key={index}
                    style={index === 0 ? { paddingTop: '10rem' } : {}}
                  >
                    <Card
                      key={index}
                      development={development}
                      global_data={global_data}
                    />
                  </Col>
                ))}
          </Row>
        </Container>
      </section>
    )
  )
}

export default GridDevelopments7
